import React, { type FC, useEffect } from 'react';
import { MainPageSideBar } from './SubComponents/MainPageSideBar/MainPageSideBar';
import { HeadingSection } from './SubComponents/PageHeader/HeadingSection';
import { useTemplatesData } from '../../hooks/useTemplatesData';
import { usePageNavigate } from '../../hooks/usePageNavigate';
import { useTabs } from '../../hooks/useTabs';
import { AppLoader } from '../App/AppLoader';
import { ModalLoader } from '../ModalLoader';
import { DataHooks } from '../../constants/dataHooks';
import { filterToUrlParamMapper } from '../../utils/urlParams-utils';
import { useTemplatesPageBi } from '../../hooks/useTemplatesPageBi';
import { Box, Divider } from '@wix/design-system';
import type { AppTemplate } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { TemplatesGrid } from '../CommonComponents/TemplatesGrid/TemplatesGrid';
import {
  getTemplatesTabsToDisplay,
  getTemplatesForTab,
  isCliScratchTemplate,
  isSelfHostedScratchTemplate,
} from '../../utils/templateSelector/templatesSelector';
import { TemplatesGridHeading } from './SubComponents/TemplatesGridHeading';
import { ActiveModal } from '../../types/ActiveModal';
import { useCreateApp } from '../../hooks/use-create-app/useCreateApp';
import { goToApp } from '../../utils/navigation';
import { useTranslation } from '@wix/wix-i18n-config';
import { useExperiments } from '@wix/fe-essentials/react';
import { shouldDisplayHarmonyNotifications } from '../../utils/experiments';
import css from './TemplatesMainPage.scss';

export const TemplatesMainPage: FC = () => {
  const { templates, templatesLoaded, setActiveModal, setCurrentTemplate } =
    useTemplatesData();
  const { navigateToTemplate, getExternalTemplateUrl, shouldHideSidebar } =
    usePageNavigate();
  const { t } = useTranslation();
  const { createApp, isCreatingApp } = useCreateApp();
  const { activeTabId, sectionRefs, setupIntersectionObserver } = useTabs();
  const { reportClickOnTemplate } = useTemplatesPageBi();
  const { experiments } = useExperiments({ readOnly: true });
  const displayHarmonyNotifications =
    shouldDisplayHarmonyNotifications(experiments);
  const mainGridRef = React.useRef<HTMLDivElement>(null);
  const originTab = filterToUrlParamMapper?.[activeTabId];

  const handleTemplateClick = (template: AppTemplate) => {
    reportClickOnTemplate(template.id, getExternalTemplateUrl(template));
    if (isCliScratchTemplate(template)) {
      handleCliTemplateClick(template);
    } else if (isSelfHostedScratchTemplate(template)) {
      handleSelfHostedTemplateClick(template);
    } else {
      handleRegularTemplateClick(template);
    }
  };

  const handleCliTemplateClick = (template: AppTemplate) => {
    setCurrentTemplate(template);
    setActiveModal(ActiveModal.CLI);
  };

  const handleSelfHostedTemplateClick = async (template: AppTemplate) => {
    setCurrentTemplate(template);
    try {
      const { appId } = await createApp();
      if (appId) {
        goToApp(appId, 'home', true);
      }
    } catch (error) {
      console.error('Failed to create app:', error);
    }
  };

  const handleRegularTemplateClick = (template: AppTemplate) => {
    navigateToTemplate(template, originTab);
  };

  const templatesTabs = getTemplatesTabsToDisplay({
    filterByTab: shouldHideSidebar ? activeTabId : undefined,
    displayHarmonyNotifications,
  });

  useEffect(() => {
    setCurrentTemplate(undefined);
  }, []);

  useEffect(() => setupIntersectionObserver(), [setupIntersectionObserver]);

  if (!templatesLoaded) {
    return <AppLoader />;
  }

  return (
    <div data-hook={DataHooks.MainPage}>
      <HeadingSection />
      <Divider />
      <Box align={shouldHideSidebar ? 'center' : 'left'}>
        {!shouldHideSidebar && (
          <>
            <MainPageSideBar mainGridRef={mainGridRef} />
            <Divider direction="vertical" />
          </>
        )}
        <Box
          dataHook={DataHooks.MainPageGridLayout}
          gap="40px"
          width="100%"
          align="center"
          minHeight="600px"
          direction="vertical"
          padding="24px 48px 60px"
          ref={mainGridRef}
          maxWidth={shouldHideSidebar ? '1284px' : ''}
        >
          <>
            {templatesTabs.map((tab) => (
              <Box
                key={tab}
                gap="40px"
                width="100%"
                align="center"
                direction="vertical"
                position="relative"
                ref={sectionRefs[tab]}
              >
                <div id={`scroll-anchor-${tab}`} className={css.scrollTarget} />
                <TemplatesGridHeading activeTabId={tab} />
                <TemplatesGrid
                  templates={getTemplatesForTab({ templates, tab })}
                  onTemplateClick={handleTemplateClick}
                />
              </Box>
            ))}
          </>
        </Box>
      </Box>
      {isCreatingApp && (
        <ModalLoader text={t('app.templates.self-hosted.creating-app')} />
      )}
    </div>
  );
};
